import {useState, useEffect, useRef} from 'react';
import useUrlParameter from '@/components/search/common/hooks/useUrlParameter';
import {SectionHeader} from '@/components/main/common';
import FilterGroup from './FilterGroup';
import {OPTION_LABELS} from '@/constants/search/filter';

const getDefaultOptions = (sortItems) =>
  sortItems.map((group) => {
    const defaultOption =
      group.options.find((option) => option.default) || group.options[0];
    return {
      key: group.key,
      ...defaultOption,
    };
  });

const FilterBox = ({title, sortItems, onCallback}) => {
  const {getParams} = useUrlParameter();
  const {tagname = ''} = getParams();
  const calenderRef = useRef(null);

  const [isOpen, setIsOpen] = useState(false);
  const [dateRange, setDateRange] = useState({startDate: '', endDate: ''});
  const [localSortItems, setLocalSortItems] = useState(sortItems);
  const [selectedOptions, setSelectedOptions] = useState(() =>
    getDefaultOptions(sortItems),
  );

  // 선택된 필터 옵션 업데이트
  const updateSelectedOptions = (options, filterGroupKey, selectedOption) =>
    options.map((option) =>
      option.key === filterGroupKey
        ? {
            key: filterGroupKey,
            ...selectedOption,
            // type이 directInput인 경우에만 startDate, endDate, applyStatus 유지
            ...(selectedOption.type === 'directInput'
              ? {
                  startDate: selectedOption.startDate || '',
                  endDate: selectedOption.endDate || '',
                  applyStatus: selectedOption.applyStatus || false,
                }
              : {}),
          }
        : option,
    );

  // 기본값 상태(=default값) 업데이트
  const updateSortItemDefaults = (items, filterGroupKey, optionIndex) =>
    items.map((group) => {
      if (group.key !== filterGroupKey) {
        return group;
      }

      return {
        ...group,
        options: group.options.map((option, idx) => ({
          ...option,
          default: idx === optionIndex,
        })),
      };
    });

  // 필터 옵션 클릭시 핸들러
  const handleOptionSelect = (filterGroupKey, optionIndex, selectedOption) => {
    setSelectedOptions((prev) =>
      updateSelectedOptions(prev, filterGroupKey, selectedOption),
    );

    setLocalSortItems((prev) =>
      updateSortItemDefaults(prev, filterGroupKey, optionIndex),
    );
  };

  // 날짜 필터 옵션 처리 핸들러
  const handleDateOption = ({
    filterGroupKey,
    optionIndex,
    option,
    startDate,
    endDate,
  }) => {
    // 날짜가 없으면 캘린더만 열기
    if (!startDate && !endDate) {
      calenderRef?.current?.open();
      return;
    }

    // 날짜가 있으면 상태 업데이트
    setDateRange({startDate, endDate});

    const updatedOption = {
      ...option,
      startDate,
      endDate,
    };

    handleOptionSelect(filterGroupKey, optionIndex, updatedOption);
  };

  // 캘린더 적용 버튼 클릭 핸들러
  const handleDateSelected = ({
    filterGroupKey,
    optionIndex,
    option,
    startDate,
    endDate,
  }) => {
    handleDateOption({filterGroupKey, optionIndex, option, startDate, endDate});
  };

  // 직접 입력 클릭 핸들러
  const handleDirectInput = (filterGroupKey, optionIndex, selectedOption) => {
    handleDateOption({
      filterGroupKey,
      optionIndex,
      option: selectedOption,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    });
  };

  // 옵션 비활성화 상태 체크
  const getDisabledState = (optionLabel) => {
    const isPlaylistDisabled = selectedOptions.some(
      (option) => option.label === OPTION_LABELS.UP_COUNT,
    );
    const isUpDisabled = selectedOptions.some(
      (option) => option.label === OPTION_LABELS.PLAYLIST,
    );

    if (optionLabel === OPTION_LABELS.PLAYLIST) {
      return isPlaylistDisabled;
    }
    if (optionLabel === OPTION_LABELS.UP_COUNT) {
      return isUpDisabled;
    }
    return false;
  };

  // 옵션 클래스 결정
  const getOptionClass = (option) => {
    if (option.default) {
      return 'active';
    }
    if (getDisabledState(option.label)) {
      return 'disabled';
    }
    return '';
  };

  useEffect(() => {
    onCallback?.(selectedOptions);
  }, [selectedOptions]);

  if (localSortItems.length < 1) {
    return null;
  }

  return (
    <>
      <SectionHeader
        title={title}
        rightElement={
          <div className={`select_box_item ${isOpen ? 'on' : ''}`}>
            <button
              type='button'
              className='selected'
              onClick={() => setIsOpen((prev) => !prev)}>
              {selectedOptions.map((option, index) => (
                <span key={`select_option_${index}`} className='item'>
                  {option.label}
                </span>
              ))}
            </button>
          </div>
        }
      />
      <div className={`select_filter_list ${isOpen ? 'active' : ''}`}>
        {localSortItems.map((filterGroup, index) => (
          <FilterGroup
            key={`${filterGroup.key}_${index}`}
            filterGroup={filterGroup}
            tagname={tagname}
            calenderRef={calenderRef}
            dateRange={dateRange}
            getOptionClass={getOptionClass}
            handleDirectInput={handleDirectInput}
            handleOptionSelect={handleOptionSelect}
            handleDateSelected={handleDateSelected}
          />
        ))}
        <button
          type='button'
          className='btn_close'
          onClick={() => setIsOpen(false)}>
          레이어 닫기
        </button>
      </div>
    </>
  );
};

export default FilterBox;
